import { reportErrorToPosthog } from "@fideus/analytics";
import { createAuthressAuthProvider, SignedUpGuard } from "@fideus/auth";
import { ConsoleLogger } from "@fideus/logger";
import {
  ErrorBoundary,
  LightThemeProvider,
  LoadingPage,
} from "@fideus-ui/core";
import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider } from "react-i18next";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { createPosthogAnalyticsProvider, initPosthog } from "./_app/analytics";
import {
  reportAppLoadDiagnostics,
  snapshotAuthState,
} from "./_app/analytics/appLoadDiagnostics";
import type { Event } from "./_app/analytics/events/Event";
import { TrpcApiProvider } from "./_app/api";
import { ComposedProvider } from "./_app/ComposedProvider";
import { ConfigProvider, getConfig, initConfig } from "./_app/config";
import { createPosthogFeatureFlagProvider } from "./_app/featureFlags";
import { i18n, i18nInitPromise } from "./_app/i18n";
import { createAppStore, createPersistor } from "./_app/redux";
import { App } from "./App";

// Start config loading eagerly during module evaluation so the fetch
// aligns with the <link rel="preload"> in index.html. Without this,
// the fetch only begins when init() is called, wasting any head-start
// the preload provides.
const configInitPromise = initConfig();

async function init() {
  const t0 = performance.now();
  const authState = snapshotAuthState();

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  await configInitPromise;
  const t1 = performance.now();

  await i18nInitPromise;
  const t2 = performance.now();

  const config = getConfig();
  let posthog = null;

  if (!config.postHog && !import.meta.env.DEV) {
    throw new Error("PostHog config is not set");
  }

  if (config.postHog) {
    posthog = initPosthog(config.postHog);
  }

  const providers = await Promise.all([
    ConfigProvider,
    LightThemeProvider,
    createPosthogAnalyticsProvider<Event>({
      posthog,
      application: config.name,
      applicationVersion: config.version ?? "unknown",
      userType: "service",
    }),
    createPosthogFeatureFlagProvider({
      posthog,
      flags: config.featureFlags,
    }),
    createAuthressAuthProvider({
      authressApiUrl: config.loginUrl,
      authressApplicationId: config.authressApplicationId,
      loadingFallback: <LoadingPage loadingText="Login wird vorbereitet..." />,
      audiences: [config.apiUrl],
      connectionId: "google",
    }),
  ]);
  const t3 = performance.now();

  const store = createAppStore();

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <ErrorBoundary onError={reportErrorToPosthog}>
          <ReduxProvider store={store}>
            <PersistGate
              loading={<LoadingPage />}
              persistor={createPersistor(store)}
            >
              <ComposedProvider providers={providers}>
                <SignedUpGuard
                  loadingFallback={
                    <LoadingPage loadingText="Login wird vorbereitet..." />
                  }
                >
                  <TrpcApiProvider apiUrl={config.apiUrl}>
                    <HelmetProvider>
                      <App />
                    </HelmetProvider>
                  </TrpcApiProvider>
                </SignedUpGuard>
              </ComposedProvider>
            </PersistGate>
          </ReduxProvider>
        </ErrorBoundary>
      </I18nextProvider>
    </React.StrictMode>
  );

  reportAppLoadDiagnostics(
    {
      configLoadMs: Math.round(t1 - t0),
      i18nLoadMs: Math.round(t2 - t1),
      providerCreationMs: Math.round(t3 - t2),
      totalInitMs: Math.round(t3 - t0),
    },
    authState
  );
}

void init().catch((error) => {
  const logger = new ConsoleLogger();
  logger.info("Initialization error:", error);
});
